import React, { useEffect, useState } from 'react';
import {
    Box, Container, Typography, Divider, Stack, CircularProgress, Paper,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { sttApi } from '../api/sttApi';

function FeatureItem({ icon, title, description, secondary }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            {icon}
            <Box>
                <Typography variant="body1" fontWeight={600} color={secondary ? 'text.secondary' : 'text.primary'}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.25}>
                    {description}
                </Typography>
            </Box>
        </Box>
    );
}

export default function RoadmapPage() {
    const [roadmap, setRoadmap] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        sttApi.getRoadmap()
            .then(setRoadmap)
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, []);

    return (
        <Container maxWidth="md" sx={{ py: 6 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
                Roadmap
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={4}>
                An overview of currently available features and what's coming next.
            </Typography>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Typography color="text.secondary">
                    Failed to load the roadmap. Please try again later.
                </Typography>
            )}

            {roadmap && (
                <Stack spacing={4}>
                    {/* Available */}
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="overline" color="success.main" fontWeight={700} letterSpacing={1.2}>
                            Available now
                        </Typography>
                        <Divider sx={{ my: 1.5 }} />
                        <Stack spacing={2.5} mt={1}>
                            {roadmap.available?.map((item) => (
                                <FeatureItem
                                    key={item.id}
                                    icon={
                                        <CheckCircleOutlineIcon
                                            sx={{ fontSize: 22, color: 'success.main', mt: '2px', flexShrink: 0 }}
                                        />
                                    }
                                    title={item.title}
                                    description={item.description}
                                />
                            ))}
                        </Stack>
                    </Paper>

                    {/* Planned */}
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="overline" color="text.disabled" fontWeight={700} letterSpacing={1.2}>
                            Coming soon
                        </Typography>
                        <Divider sx={{ my: 1.5 }} />
                        <Stack spacing={2.5} mt={1}>
                            {roadmap.planned?.map((item) => (
                                <FeatureItem
                                    key={item.id}
                                    secondary
                                    icon={
                                        <RadioButtonUncheckedIcon
                                            sx={{ fontSize: 22, color: 'text.disabled', mt: '2px', flexShrink: 0 }}
                                        />
                                    }
                                    title={item.title}
                                    description={item.description}
                                />
                            ))}
                        </Stack>
                    </Paper>

                    {roadmap.updated && (
                        <Typography variant="caption" color="text.disabled" align="right" display="block">
                            Last updated: {roadmap.updated}
                        </Typography>
                    )}
                </Stack>
            )}
        </Container>
    );
}
